/**
 * Cloud Operations — Operations log.
 *
 * Unified audit-ish view of every Cloud Operations action: deployment
 * lifecycle, run dispatch + completion, target create/retire, cloud
 * account create/verify/revoke, bridge register/revoke. PA cross-customer,
 * SA scoped.
 *
 * Filters: action prefix, actor, target id, time window. CSV export of the
 * current filter (capped at 2000 rows server-side).
 *
 * Honest scope note: v1 aggregates from operator entity rows + run rows,
 * not from a dedicated audit chain. Adequate for "what happened?"; not yet
 * adequate for tamper-evident attestation. The Verify Chain button is wired
 * but greyed until the chain pipeline lands.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  listOperationsAudit,
  listAuditChains,
  verifyAuditChain,
} from '../../services/cloudOperationsApi';
import {
  T, btn, btnSm, btnPrimary, inp, Field, Banner, Slideout, Th, Td, fmtDate, shortId,
} from './_ui.jsx';

const ACTION_GROUPS = [
  { id: '',                label: 'All actions' },
  { id: 'run',             label: 'Runs (start / success / failed)' },
  { id: 'deployment',      label: 'Deployments' },
  { id: 'target',          label: 'Deployment targets' },
  { id: 'cloud_account',   label: 'Cloud accounts' },
  { id: 'bridge',          label: 'Bridge agents' },
];

export default function OperationsLogPage() {
  const [filters, setFilters] = useState({
    action: '', actor_id: '', target_id: '', time_after: '', time_before: '',
  });
  const [rows, setRows] = useState([]);
  const [page, setPage]   = useState(0);
  const [pageInfo, setPageInfo] = useState({ total_elements: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    listOperationsAudit({ ...trimEmpty(filters), page, size: 50 })
      .then((r) => {
        setRows(r?.items || []);
        setPageInfo({ total_elements: r?.total_elements ?? 0, total_pages: r?.total_pages ?? 1 });
      })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters, page]);

  const downloadCsv = () => {
    const params = new URLSearchParams(trimEmpty(filters)).toString();
    // Use a regular link click — fetch+blob is overkill for a simple GET CSV.
    window.location.href = `/api/v1/cloud-operations/audit/export.csv?${params}`;
  };

  return (
    <div>
      <Header onExport={downloadCsv} onVerifyChain={() => setVerifyOpen(true)} />
      <VerifyChainSlideout open={verifyOpen} onClose={() => setVerifyOpen(false)} />
      <Filters value={filters} onChange={(patch) => { setFilters({ ...filters, ...patch }); setPage(0); }} />

      {error && <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>}
      {loading && rows.length === 0 ? (
        <Banner color={T.info} bg={T.infoBg}>Loading audit entries…</Banner>
      ) : rows.length === 0 ? (
        <Empty />
      ) : (
        <>
          <Table rows={rows} />
          <Pagination page={page} totalPages={pageInfo.total_pages} totalElements={pageInfo.total_elements} onChange={setPage} />
        </>
      )}
    </div>
  );
}

function Header({ onExport, onVerifyChain }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, color: T.ink, fontWeight: 700 }}>Operations log</h2>
        <div style={{ color: T.text2, fontSize: 13, marginTop: 4 }}>
          Every Cloud Operations action — deployments, runs, targets, cloud accounts, bridges. Filter and export for an auditor.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onVerifyChain} style={btn}>
          Verify chain integrity
        </button>
        <button type="button" onClick={onExport} style={btnPrimary}>Export CSV</button>
      </div>
    </div>
  );
}

function VerifyChainSlideout({ open, onClose }) {
  const [chains, setChains] = useState([]);
  const [selected, setSelected] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    listAuditChains()
      .then((r) => {
        const items = r?.items || [];
        setChains(items);
        // Auto-select if there's only one chain (SA case).
        if (items.length === 1) setSelected(items[0].chain_key);
        else setSelected('');
      })
      .catch((e) => setError(e?.message || String(e)));
  }, [open]);

  const run = async () => {
    if (!selected) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await verifyAuditChain(selected);
      setResult(r);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Slideout open={open} onClose={onClose} width={600}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.stroke}` }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>Audit chain integrity</div>
        <div style={{ fontSize: 12, color: T.text2, marginTop: 4 }}>
          Walks the SHA-256 chain over every audit_log row since V270. Pre-V270 rows are uncovered and surfaced separately.
        </div>
      </div>

      <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
        {error && <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>}

        <Field label="Chain" hint={chains.length <= 1 ? 'Verifying your tenant chain.' : 'Platform Admin can pick any chain.'}>
          <select style={inp} value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">— select —</option>
            {chains.map((c) => (
              <option key={c.chain_key} value={c.chain_key}>
                {c.chain_key}{c.row_count != null ? ` (${c.row_count} rows)` : ''}
              </option>
            ))}
          </select>
        </Field>

        {result && <ResultPanel result={result} />}
      </div>

      <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.stroke}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={btn}>Close</button>
        <button type="button" onClick={run} disabled={!selected || running} style={btnPrimary}>
          {running ? 'Verifying…' : 'Run verifier'}
        </button>
      </div>
    </Slideout>
  );
}

function ResultPanel({ result }) {
  if (result.error) {
    return <Banner color={T.danger} bg={T.dangerBg}>Verifier error: {result.error}</Banner>;
  }
  if (result.intact) {
    return (
      <div style={{ marginTop: 16 }}>
        <Banner color={T.success} bg={T.successBg}>
          ✓ Chain intact — {result.verified_rows.toLocaleString()} of {result.total_rows.toLocaleString()} rows verified.
        </Banner>
        {result.unverified_legacy_rows > 0 && (
          <Banner color={T.warn} bg={T.warnBg}>
            {result.unverified_legacy_rows.toLocaleString()} pre-V270 legacy rows exist on this tenant
            and were not part of the chain. They are not "broken" — they were written before chain columns existed.
          </Banner>
        )}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 16 }}>
      <Banner color={T.danger} bg={T.dangerBg}>
        ✗ Chain BROKEN at row {shortId(result.first_break_row_id)} ({result.break_kind}).
      </Banner>
      <div style={{
        background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6,
        padding: 12, fontSize: 12, color: T.text2,
      }}>
        <Row label="Verified before break" value={`${result.verified_rows.toLocaleString()} of ${result.total_rows.toLocaleString()}`} />
        <Row label="Broken row id"       value={result.first_break_row_id} />
        <Row label="Broken at"           value={fmtDate(result.first_break_created_at)} />
        <Row label="Break kind"          value={result.break_kind} />
        {result.expected_prev != null && <Row label="Expected prev_hash" value={result.expected_prev} mono />}
        {result.actual_prev   != null && <Row label="Actual prev_hash"   value={result.actual_prev}   mono />}
        {result.expected_content != null && <Row label="Expected content_hash" value={result.expected_content} mono />}
        {result.actual_content   != null && <Row label="Actual content_hash"   value={result.actual_content}   mono />}
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, padding: '4px 0' }}>
      <div style={{ color: T.text3, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit', wordBreak: 'break-all', color: T.ink }}>
        {String(value)}
      </div>
    </div>
  );
}

function Filters({ value, onChange }) {
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 8,
      padding: 14, marginBottom: 14,
      display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12,
    }}>
      <Field label="Action">
        <select style={inp} value={value.action} onChange={(e) => onChange({ action: e.target.value })}>
          {ACTION_GROUPS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
        </select>
      </Field>
      <Field label="Actor (user UUID)">
        <input style={inp} placeholder="optional"
          value={value.actor_id}
          onChange={(e) => onChange({ actor_id: e.target.value })} />
      </Field>
      <Field label="Target (UUID)">
        <input style={inp} placeholder="deployment / target / account / bridge / run"
          value={value.target_id}
          onChange={(e) => onChange({ target_id: e.target.value })} />
      </Field>
      <Field label="After">
        <input style={inp} type="datetime-local" value={value.time_after}
          onChange={(e) => onChange({ time_after: e.target.value })} />
      </Field>
      <Field label="Before">
        <input style={inp} type="datetime-local" value={value.time_before}
          onChange={(e) => onChange({ time_before: e.target.value })} />
      </Field>
    </div>
  );
}

function Table({ rows }) {
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead style={{ background: T.bgTint }}>
          <tr>
            <Th>Timestamp</Th>
            <Th>Action</Th>
            <Th>Target</Th>
            <Th>Outcome</Th>
            <Th>Actor</Th>
            <Th>Details</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e, idx) => (
            <tr key={`${e.timestamp}:${e.action}:${idx}`} style={{ borderTop: `1px solid ${T.stroke2}` }}>
              <Td style={{ whiteSpace: 'nowrap', color: T.text2 }}>{fmtDate(e.timestamp)}</Td>
              <Td><ActionChip action={e.action} /></Td>
              <Td>
                <div style={{ fontWeight: 600, color: T.ink }}>{e.target_kind}</div>
                <div style={{ fontSize: 11, color: T.text3 }}>{shortId(e.target_id)}</div>
              </Td>
              <Td><OutcomeChip outcome={e.outcome} /></Td>
              <Td style={{ color: T.text2 }}>{shortId(e.actor_id)}</Td>
              <Td>
                <DetailsCell details={e.details} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionChip({ action }) {
  const family = (action || '').split('.')[0];
  const colors = {
    run:           { fg: '#1f5fa6', bg: '#ecf3f8' },
    deployment:    { fg: '#0e3552', bg: '#e8f0fb' },
    target:        { fg: '#7a4d00', bg: '#fff4d9' },
    cloud_account: { fg: '#2e7d4f', bg: '#e7f4ec' },
    bridge:        { fg: '#6b3f9e', bg: '#f3edf8' },
  };
  const c = colors[family] || { fg: T.text2, bg: T.bgAlt };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      border: `1px solid ${c.fg}33`,
    }}>{action}</span>
  );
}

function OutcomeChip({ outcome }) {
  const map = {
    success:       { fg: T.success, bg: T.successBg },
    healthy:       { fg: T.success, bg: T.successBg },
    active:        { fg: T.success, bg: T.successBg },
    connected:     { fg: T.success, bg: T.successBg },
    in_progress:   { fg: T.info,    bg: T.infoBg },
    pending:       { fg: T.warn,    bg: T.warnBg },
    failed:        { fg: T.danger,  bg: T.dangerBg },
    error:         { fg: T.danger,  bg: T.dangerBg },
    revoked:       { fg: T.danger,  bg: T.dangerBg },
    retired:       { fg: T.text3,   bg: T.bgAlt },
  };
  const c = map[outcome] || { fg: T.text2, bg: T.bgAlt };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      border: `1px solid ${c.fg}33`,
    }}>{outcome || '—'}</span>
  );
}

function DetailsCell({ details }) {
  const entries = useMemo(() => {
    if (!details || typeof details !== 'object') return [];
    return Object.entries(details).filter(([, v]) => v !== '' && v != null);
  }, [details]);
  if (entries.length === 0) return <span style={{ color: T.text3 }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11, color: T.text2 }}>
      {entries.slice(0, 4).map(([k, v]) => (
        <span key={k} style={{
          background: T.bgAlt, borderRadius: 3, padding: '1px 6px',
        }}>
          <span style={{ color: T.text3 }}>{k}:</span> {String(v)}
        </span>
      ))}
      {entries.length > 4 && (
        <span style={{ color: T.text3 }}>+{entries.length - 4} more</span>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, totalElements, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 4px', marginTop: 10, fontSize: 12, color: T.text2,
    }}>
      <div>{totalElements.toLocaleString()} entries · page {page + 1} of {totalPages || 1}</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" style={btnSm} disabled={page === 0}
          onClick={() => onChange(Math.max(0, page - 1))}>← Prev</button>
        <button type="button" style={btnSm} disabled={page + 1 >= (totalPages || 1)}
          onClick={() => onChange(page + 1)}>Next →</button>
      </div>
    </div>
  );
}

function Empty() {
  return (
    <div style={{
      background: T.bgCard, border: `1px dashed ${T.stroke}`,
      borderRadius: 10, padding: '40px 24px', textAlign: 'center', color: T.text2,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 6 }}>
        No matching audit entries
      </div>
      <div style={{ fontSize: 13 }}>
        Try widening the date window or clearing filters. New events appear here as deployments run.
      </div>
    </div>
  );
}

function trimEmpty(o) {
  const out = {};
  Object.entries(o).forEach(([k, v]) => { if (v != null && v !== '') out[k] = v; });
  return out;
}
